import { marketOf, type MarketFacts } from '@makan/contracts/client';
import { locale as language } from './strings.ts';

/**
 * How a page writes what depends on the market: money, counts, dates and
 * times, in the market's locale, zone and currency (the row of MARKETS in
 * @makan/contracts), in the page's language: zh writes the market's dates in
 * Simplified Chinese ("2026年10月19日"), in the same zone, and money keeps the
 * market's symbol ("RM5"). The one place a page's Intl lives: a page never spells a
 * locale, a zone or an offset (E09), it asks `marketFormat(market)`.
 *
 * Amounts are integer minor units, as payloads hold them (`sen`, `budgetSen`).
 * An instant is an RFC 3339 string (or a Date); a calendar date is
 * `YYYY-MM-DD`, written as the group's calendar knows it, so it is shown as
 * written, in no zone.
 */
export interface MarketFormat {
  readonly facts: MarketFacts;
  /** An amount with its currency symbol, whole when it has no cents: "RM5", "RM2.50", "S$1,200". */
  readonly money: (minor: number) => string;
  /** An amount held, a balance, always with its cents: "RM0.00", "RM12.50". */
  readonly balance: (minor: number) => string;
  /** An amount as staff type it in the currency ("5", "2.50") in minor units; null when it is none or not above zero. */
  readonly parseMoney: (typed: string) => number | null;
  /** A count: "1,234". */
  readonly count: (value: number) => string;
  /** An instant's date in the market's zone: "19 Oct 2026". */
  readonly date: (instant: string | Date) => string;
  /** An instant's date and 24-hour time in the market's zone: "19 Oct 2026, 14:05". */
  readonly dateTime: (instant: string | Date) => string;
  /** An instant's 24-hour time in the market's zone: "14:05". */
  readonly time: (instant: string | Date) => string;
  /** A calendar date: "19 Oct 2026". */
  readonly day: (date: string) => string;
  /** A calendar date without its year: "19 Oct". */
  readonly dayMonth: (date: string) => string;
  /** Today's calendar date in the market's zone, `YYYY-MM-DD` (or the date of `now`). */
  readonly today: (now?: Date) => string;
  /** The instant a calendar date starts in the market: "2026-10-19T00:00:00+08:00". */
  readonly startOf: (date: string) => string;
}

const made = new Map<string, MarketFormat>();

/**
 * The format of a market, made once per market and kept: a page may call it
 * on every render. A page of no group (the showroom) passes nothing and
 * writes as a group that set no market does (marketOf).
 */
export function marketFormat(facts: MarketFacts = marketOf(undefined)): MarketFormat {
  const kept = made.get(facts.market);
  if (kept !== undefined && kept.facts === facts) return kept;
  const format = formatOf(facts);
  made.set(facts.market, format);
  return format;
}

/** The Intl locale of a market in the page's language: the market's own (en-MY), or Simplified Chinese (zh-Hans-MY) or Malay (ms-MY) in its region. */
function intlLocale(facts: MarketFacts): string {
  switch (language()) {
    case 'en':
      return facts.locale;
    case 'zh':
      return `zh-Hans-${facts.market}`;
    case 'ms':
      return `ms-${facts.market}`;
  }
}

function formatOf(facts: MarketFacts): MarketFormat {
  const { locale, timezone: timeZone, currency } = facts;
  // Dates and times are written in the page's language; amounts, counts and the machine's calendar in the market's own locale.
  const words = intlLocale(facts);
  const scale = 10 ** currency.minorUnits;
  const whole = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const cents = new Intl.NumberFormat(locale, { minimumFractionDigits: currency.minorUnits, maximumFractionDigits: currency.minorUnits });
  const count = new Intl.NumberFormat(locale);
  const date = new Intl.DateTimeFormat(words, { day: 'numeric', month: 'short', year: 'numeric', timeZone });
  const dateTime = new Intl.DateTimeFormat(words, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
  const time = new Intl.DateTimeFormat(words, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
  // A calendar date is shown as written: read it at UTC midnight and format it in UTC.
  const day = new Intl.DateTimeFormat(words, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  const dayMonth = new Intl.DateTimeFormat(words, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const calendar = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', timeZone });
  const typed = new RegExp(`^\\d+(\\.\\d{1,${String(currency.minorUnits)}})?$`);
  const offset = offsetOf(facts.utcOffsetMinutes);
  const asWritten = (calendarDate: string) => new Date(`${calendarDate}T00:00:00Z`);
  return {
    facts,
    money: (minor) => `${currency.symbol}${(minor % scale === 0 ? whole : cents).format(minor / scale)}`,
    balance: (minor) => `${currency.symbol}${cents.format(minor / scale)}`,
    parseMoney: (text) => {
      const trimmed = text.trim();
      if (!typed.test(trimmed)) return null;
      const minor = Math.round(Number(trimmed) * scale);
      return minor > 0 ? minor : null;
    },
    count: (value) => count.format(value),
    date: (instant) => date.format(new Date(instant)),
    dateTime: (instant) => dateTime.format(new Date(instant)),
    time: (instant) => time.format(new Date(instant)),
    day: (calendarDate) => day.format(asWritten(calendarDate)),
    dayMonth: (calendarDate) => dayMonth.format(asWritten(calendarDate)),
    today: (now = new Date()) => {
      const parts = Object.fromEntries(calendar.formatToParts(now).map((part) => [part.type, part.value]));
      return `${parts['year'] ?? ''}-${parts['month'] ?? ''}-${parts['day'] ?? ''}`;
    },
    startOf: (calendarDate) => `${calendarDate}T00:00:00${offset}`,
  };
}

/** +480 -> "+08:00". */
function offsetOf(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}
